import {
  ensureCablesRuntime,
  observeProximity,
  runAfterWebflowInteractions,
  runWhenIdle,
} from "../shared/cables-runtime";
import {
  isProductAnimationSource,
  PRODUCT_ANIMATION_SOURCE,
  setProductAnimationState,
} from "./animation-state";

const PRODUCT_SCENE_PATCH_URL =
  "https://d2my2wpsc41l6t.cloudfront.net/modernexpo/js/ModernExpo_-_Product_Slider_4.gz.json";
const TEXTURE_ARRAY_LOADING_PROGRESS = 0.95;

export function initSceneScroller({
  wrapper = document.querySelector(".a-animation--wrap-secondary"),
  canvas = document.querySelector(".scene-scroller"),
} = {}) {
  const gsapInstance = window.gsap;
  const scrollTriggerPlugin = window.ScrollTrigger;

  if (
    !wrapper ||
    !canvas ||
    window.innerWidth < 1024 ||
    !window.CABLES?.Patch ||
    !gsapInstance?.utils ||
    !scrollTriggerPlugin?.create ||
    canvas.dataset.sceneScrollerInitialized === "true"
  ) {
    return null;
  }

  const stateWrapper = wrapper.closest(".a-animation--wrap-main");
  let assetsFinishedLoading = false;
  let firstFrameRendered = false;
  let loadingFailed = false;
  let patch = null;

  const hasFinishedMeaningfulLoading = () => {
    if (assetsFinishedLoading) return true;

    const loadingJobs = patch?.loading?.getList?.() ?? [];
    const unfinishedJobs = loadingJobs.filter((job) => !job.finished);
    const loadingProgress = patch?.loading?.getProgress?.() ?? 0;

    return (
      loadingProgress >= TEXTURE_ARRAY_LOADING_PROGRESS &&
      unfinishedJobs.length === 1 &&
      unfinishedJobs[0].type === "texturearray"
    );
  };

  const markReady = () => {
    if (loadingFailed || !firstFrameRendered || !hasFinishedMeaningfulLoading()) return;

    canvas.dataset.sceneScrollerAssetsReady = "true";
    canvas.dataset.sceneScrollerReady = "true";
    setProductAnimationState(stateWrapper, PRODUCT_ANIMATION_SOURCE.CABLES, "ready");
  };

  const markError = (error) => {
    loadingFailed = true;
    canvas.dataset.sceneScrollerReady = "false";

    if (isProductAnimationSource(stateWrapper, PRODUCT_ANIMATION_SOURCE.CABLES)) {
      setProductAnimationState(stateWrapper, PRODUCT_ANIMATION_SOURCE.CABLES, "error");
    }

    console.error("[CABLES] Product scene failed", error);
  };

  setProductAnimationState(stateWrapper, PRODUCT_ANIMATION_SOURCE.CABLES, "loading");

  patch = new window.CABLES.Patch({
    patchFile: PRODUCT_SCENE_PATCH_URL,
    glCanvasId: canvas.id,
    glCanvasResizeToWindow: true,
    canvas: { alpha: false, premultipliedAlpha: false },
    onFinishedLoading: () => {
      assetsFinishedLoading = true;
      markReady();
    },
    onFirstFrameRendered: () => {
      firstFrameRendered = true;
      canvas.dataset.sceneScrollerFirstFrameRendered = "true";
      markReady();
    },
    onError: markError,
  });

  patch.loading?.on?.("finishedTask", markReady);

  const scrollTrigger = scrollTriggerPlugin.create({
    trigger: wrapper,
    start: "top top",
    end: "bottom top",
    scrub: 0.6,
    invalidateOnRefresh: true,
    onUpdate: (self) => {
      const rawProgress = gsapInstance.utils.mapRange(0, 1, -50, 130, self.progress);
      const progress = gsapInstance.utils.clamp(0, 100, rawProgress);
      patch.setVarValue("progress", progress);
    },
  });

  scrollTrigger.disable(false);

  const pushInitialProgress = () => {
    if (typeof scrollTrigger.vars?.onUpdate === "function") {
      scrollTrigger.vars.onUpdate(scrollTrigger);
    }
  };

  observeProximity(
    wrapper,
    () => {
      scrollTrigger.enable();
      scrollTrigger.refresh();
      pushInitialProgress();
    },
    () => scrollTrigger.disable(false)
  );

  canvas.dataset.sceneScrollerInitialized = "true";
  return { patch, scrollTrigger };
}

function reinitializeWebflowInteractions() {
  let webflowInteractions = null;

  try {
    webflowInteractions = window.Webflow?.require?.("ix3");
  } catch {
    webflowInteractions = null;
  }

  if (typeof webflowInteractions?.emit === "function") {
    webflowInteractions.emit("first-load");
  }

  window.ScrollTrigger?.refresh?.();
}

export function bootSceneScroller() {
  const wrapper = document.querySelector(".a-animation--wrap-secondary");
  const canvas = document.querySelector(".scene-scroller");
  const stateWrapper = wrapper?.closest(".a-animation--wrap-main");

  if (
    !wrapper ||
    !canvas ||
    wrapper.dataset.sceneScrollerBootstrapped === "true" ||
    !window.gsap ||
    !window.ScrollTrigger
  ) {
    return;
  }

  wrapper.dataset.sceneScrollerBootstrapped = "true";
  window.gsap.registerPlugin(window.ScrollTrigger);

  runAfterWebflowInteractions(() => {
    let isInitializationPending = false;

    const tryInitSceneScroller = () => {
      if (
        isInitializationPending ||
        canvas.dataset.sceneScrollerInitialized === "true" ||
        window.innerWidth < 1024
      ) {
        return;
      }

      isInitializationPending = true;
      runWhenIdle(() => {
        ensureCablesRuntime()
          .then(() => {
            document.dispatchEvent(new Event("CABLES.jsLoaded"));
            initSceneScroller({ wrapper, canvas });
            window.ScrollTrigger.refresh();
            window.setTimeout(reinitializeWebflowInteractions, 350);
          })
          .catch((error) => {
            if (isProductAnimationSource(stateWrapper, PRODUCT_ANIMATION_SOURCE.CABLES)) {
              setProductAnimationState(stateWrapper, PRODUCT_ANIMATION_SOURCE.CABLES, "error");
            }
            console.error("[CABLES] Failed to initialize product scene", error);
          })
          .finally(() => {
            isInitializationPending = false;
          });
      });
    };

    tryInitSceneScroller();

    const desktopMedia = window.matchMedia("(min-width: 1024px)");
    const handleDesktopChange = (event) => {
      if (event.matches) tryInitSceneScroller();
    };

    if (desktopMedia.addEventListener) {
      desktopMedia.addEventListener("change", handleDesktopChange);
    } else if (desktopMedia.addListener) {
      desktopMedia.addListener(handleDesktopChange);
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootSceneScroller, { once: true });
} else {
  bootSceneScroller();
}
